import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// The Plans upload slideover's drop zone (TASK-265.10): several vector PDFs
// into ONE drawing set.
//
// Each PDF goes straight to storage with DirectUpload, one at a time, and its
// signed id is written into a hidden `signed_blob_ids[]` input that lives
// INSIDE the file's row — so removing a row removes the id, and a server
// re-render (a name or profile error) can put the same rows and ids back
// without a re-upload. The form is submitted once, with every id; the server
// validates each file and builds the sheets. Unlike the Files queue there is
// no per-file POST, because a drawing set is a single record.
//
// The submit button is held off while anything is still uploading, and until
// at least one file is ready.
export default class extends Controller {
  static targets = ["fileInput", "dropZone", "rows", "rowTemplate", "errorMessage", "submitButton"]

  static values = {
    directUploadUrl: String,
    extensions: Array,
    maxSizeMb: { type: Number, default: 200 }
  }

  connect() {
    this.queue = []
    this.running = false
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragLeave = this.dragLeave.bind(this)
    this.boundDrop = this.drop.bind(this)

    this.dropZoneTarget.addEventListener("dragover", this.boundDragOver)
    this.dropZoneTarget.addEventListener("dragleave", this.boundDragLeave)
    this.dropZoneTarget.addEventListener("drop", this.boundDrop)
    this.refreshSubmit()
  }

  disconnect() {
    this.dropZoneTarget.removeEventListener("dragover", this.boundDragOver)
    this.dropZoneTarget.removeEventListener("dragleave", this.boundDragLeave)
    this.dropZoneTarget.removeEventListener("drop", this.boundDrop)
  }

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-blue-500", "bg-blue-50")
    this.addFiles(event.dataTransfer.files)
  }

  selectFiles() {
    this.fileInputTarget.click()
  }

  handleFilesSelected(event) {
    this.addFiles(event.target.files)
    // Lets the same file be chosen again after it was removed.
    event.target.value = ""
  }

  addFiles(fileList) {
    const files = Array.from(fileList || [])
    if (files.length === 0) return

    const allowed = this.extensionsValue.map((e) => e.toLowerCase())
    const refused = []

    files.forEach((file) => {
      const extension = file.name.split(".").pop().toLowerCase()
      if (allowed.length > 0 && !allowed.includes(extension)) {
        refused.push(`${file.name} is not a PDF.`)
      } else if (file.size > this.maxSizeMbValue * 1024 * 1024) {
        refused.push(`${file.name} is over the ${this.maxSizeMbValue}MB limit.`)
      } else if (this.alreadyListed(file)) {
        // The same drawing twice is a slip, not an intent.
      } else {
        this.enqueue(file)
      }
    })

    if (refused.length > 0) this.showError(refused.join(" "))
    else this.clearError()

    this.refreshSubmit()
    if (!this.running) this.run()
  }

  alreadyListed(file) {
    return Array.from(this.rowsTarget.children).some(
      (row) => row.dataset.fileName === file.name && row.dataset.fileSize === String(file.size)
    )
  }

  enqueue(file) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    row.dataset.fileName = file.name
    row.dataset.fileSize = String(file.size)
    row.querySelector("[data-role='name']").textContent = file.name
    this.setState(row, "waiting", `${this.humanSize(file.size)} · Waiting`)
    this.rowsTarget.append(row)
    this.queue.push({ file, row })
  }

  async run() {
    this.running = true

    while (this.queue.length > 0) {
      const item = this.queue.shift()
      // A row removed before its turn has nothing left to upload.
      if (!item.row.isConnected) continue

      try {
        await this.upload(item)
      } catch (error) {
        this.setState(item.row, "error", "Upload failed")
      }
      this.refreshSubmit()
    }

    this.running = false
    this.refreshSubmit()
  }

  upload({ file, row }) {
    return new Promise((resolve, reject) => {
      this.setState(row, "uploading", `${this.humanSize(file.size)}`)
      const track = row.querySelector("[data-role='track']")
      const bar = row.querySelector("[data-role='bar']")
      const percent = row.querySelector("[data-role='percent']")
      track.classList.remove("hidden")
      percent.classList.remove("hidden")

      const upload = new DirectUpload(file, this.directUploadUrlValue, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            if (!event.lengthComputable) return

            const value = Math.round((event.loaded / event.total) * 100)
            bar.style.width = `${value}%`
            percent.textContent = `${value}%`
          })
        }
      })

      upload.create((error, blob) => {
        track.classList.add("hidden")
        percent.classList.add("hidden")
        if (error) return reject(error)

        const input = row.querySelector("[data-role='blob-id']")
        input.value = blob.signed_id
        input.disabled = false
        this.setState(row, "done", `${this.humanSize(file.size)} · Ready`)
        resolve()
      })
    })
  }

  remove(event) {
    event.preventDefault()
    event.target.closest("[data-upload-row]").remove()
    this.refreshSubmit()
  }

  // Ready to submit: no file still going up, and at least one id to send.
  refreshSubmit() {
    const rows = Array.from(this.rowsTarget.children)
    this.rowsTarget.classList.toggle("hidden", rows.length === 0)
    const busy = this.running || this.queue.length > 0
    const ready = rows.some((row) => {
      const input = row.querySelector("[data-role='blob-id']")
      return input && input.value && !input.disabled
    })

    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = busy || !ready
  }

  setState(row, state, meta) {
    row.querySelectorAll("[data-state]").forEach((icon) => {
      icon.classList.toggle("hidden", icon.dataset.state !== state)
    })

    const metaEl = row.querySelector("[data-role='meta']")
    metaEl.textContent = meta
    metaEl.classList.toggle("text-red-600", state === "error")
    row.querySelector("[data-role='name']").classList.toggle("text-gray-500", state === "waiting")
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
